import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, Image, HelpCircle } from 'lucide-react';
import './AdminNewsForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Função para converter links do Google Drive para formato direto
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  
  const patterns = [
    /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /https:\/\/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
    /https:\/\/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
    }
  }
  
  return url;
};

const AdminNewsForm = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    image_url: '',
    published: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadNews();
    }
  }, [id]);

  const loadNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      console.error('Erro ao carregar notícia:', error);
      setError('Erro ao carregar notícia');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Se for o campo de imagem, converter links do Google Drive automaticamente
    let processedValue = value;
    if (name === 'image_url' && value) {
      processedValue = convertGoogleDriveUrl(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEdit) {
        await axios.put(`${API}/news/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/news`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate('/admin/dashboard/news');
    } catch (error) {
      console.error('Erro ao guardar notícia:', error);
      setError('Erro ao guardar notícia. Verifique os campos e tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">A carregar...</div>;
  }

  return (
    <div className="admin-news-form">
      <div className="form-header">
        <button className="back-btn" onClick={() => navigate('/admin/dashboard/news')}>
          <ArrowLeft size={20} />
          Voltar
        </button>
        <h1>{isEdit ? 'Editar Notícia' : 'Nova Notícia'}</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="news-form">
        <div className="form-main">
          <div className="form-group">
            <label htmlFor="title">Título *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Título da notícia"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Descrição curta *</label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="Uma breve descrição para aparecer na listagem"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Conteúdo *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              placeholder="Escreva aqui o conteúdo completo da notícia..."
              rows="15"
            />
          </div>
        </div>

        <div className="form-sidebar">
          <div className="sidebar-card">
            <h3>Publicação</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleChange}
              />
              <span>Publicar notícia</span>
            </label>
            <p className="help-text">
              Se ativar esta opção, a notícia ficará visível no site.
            </p>
          </div>

          <div className="sidebar-card">
            <h3>Imagem de destaque</h3>
            <div className="form-group">
              <label htmlFor="image_url">URL da imagem</label>
              <input
                type="url"
                id="image_url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="Cole o link do Google Drive ou URL direta"
              />
              <p className="help-text google-drive-help">
                <HelpCircle size={14} />
                <span>Links do Google Drive são convertidos automaticamente. Certifique-se que a imagem está partilhada como "Qualquer pessoa com o link".</span>
              </p>
            </div>
            {formData.image_url && (
              <div className="image-preview">
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-error" style={{ display: 'none' }}>
                  <span>Imagem não disponível. Verifique se o link está correto e se a imagem está partilhada publicamente.</span>
                </div>
              </div>
            )}
          </div>

          <button type="submit" className="save-btn" disabled={saving}>
            <Save size={18} />
            {saving ? 'A guardar...' : 'Guardar Notícia'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminNewsForm;
